import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-relay';
import { CurrentTarget, useStudentActiveFocus } from '../../custom-hooks/useStudentActiveFocus';
import { NewFocusModal } from './NewFocusModal';
import { MarkStudentTargetFocusSeen } from '../../graphQLOperations/StudentTargetFocus';
import { StudentTargetFocusSeenMutation } from '../../graphQLOperations/__generated__/StudentTargetFocusSeenMutation.graphql';
import { useAppDispatch } from '../../redux/hooks';
import { setActiveStudentFocus } from '../../redux/reducers/focusSlice';

interface Props {
  currentTargets: ReadonlyArray<CurrentTarget>;
  /** Bump to force a network revalidation on homepage entry. */
  fetchKey?: string | number;
}

/**
 * Owns the student-side focus behaviour for the homepage (TEAMC-3147 / US-7):
 * reads the active focus (publishing it to redux so DashboardHeader lands the
 * student on the focused unit, even after the modal is dismissed) and shows the
 * one-time "MY NEW FOCUS" modal while it hasn't been acknowledged.
 *
 * The underlying query suspends, so render this inside a Suspense boundary and
 * only once authenticated.
 */
export const StudentFocusController: FC<Props> = ({ currentTargets, fetchKey }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Publishes the resolved focus unit to redux via its own effect (runs regardless
  // of whether the modal shows, so the homepage always lands on the focus unit).
  const { activeStudentFocus } = useStudentActiveFocus({ currentTargets, fetchKey });
  const [commitMarkFocusSeen] = useMutation<StudentTargetFocusSeenMutation>(MarkStudentTargetFocusSeen);
  // Local dismissal so the CTA closes the modal instantly — independent of the
  // mutation's outcome, so a slow/failed write never traps the student behind a
  // modal that has no other exit. Resets on remount (next homepage visit), so a
  // failed write (focusSeenOn still null server-side) re-shows it (Option 1).
  const [dismissed, setDismissed] = useState(false);

  if (!activeStudentFocus || activeStudentFocus.focusSeenOn != null || dismissed) return null;

  const handleStartNewFocus = () => {
    setDismissed(true);
    // Land on the focus unit ('/' resolves to it); replace so Back skips the old unit.
    navigate('/', { replace: true });
    dispatch(setActiveStudentFocus({ ...activeStudentFocus, focusSeenOn: new Date().toISOString() }));
    commitMarkFocusSeen({
      variables: { input: { studentTargetFocusId: activeStudentFocus.id } },
      onError: error => {
        console.error('[unit-focus] markStudentTargetFocusSeen failed', error);
      },
    });
  };

  return <NewFocusModal visible unit={activeStudentFocus.unit} onStart={handleStartNewFocus} />;
};
